/**
 * SignedOutPage — shown after a successful sign-out instead of bouncing
 * straight back to the Microsoft sign-in prompt. Confirms the user is
 * out, and gives them a clear "Sign back in" button when they're ready.
 *
 * Layout mirrors SignInPage — same photo background, same centered
 * white card, same logo/heading/subtitle rhythm — so the two screens
 * feel like siblings of the same brand moment. The only differences
 * are the copy and the button: we use a <button> (not an <a>) so we
 * can call clearJustSignedOut locally and flip React state to the
 * sign-in screen without a full redirect.
 */
import { useAuth } from '../context/AuthContext.tsx';

export function SignedOutPage() {
  const { clearJustSignedOut } = useAuth();

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-12">
      {/* Photo background + gentle gradient overlay — same layer stack
          as SignInPage so the transition in and out of the session
          reads as one continuous moment. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(43, 42, 47, 0.25) 0%, rgba(43, 42, 47, 0.45) 100%), url('/login-bg.jpg')",
          backgroundColor: '#375542', // pine fallback if image fails
        }}
      />

      {/* Card — mirrors mockup .signin-card on signed-out.html:
          identical chrome to the sign-in card so the two pages
          feel like siblings of one moment. Bone bg, no border,
          24px radius, charcoal 30% drop shadow, asymmetric
          padding 40/36/32. */}
      <div className="relative z-10 w-full max-w-[460px] rounded-[24px] bg-bone px-9 pb-8 pt-10 text-center shadow-[0_24px_60px_rgba(43,42,47,0.30)]">
        {/* Logo — 48px tall, mb-5 to the H1. */}
        <img
          src="/ojb-logo-charcoal.png"
          alt="OJB"
          className="mx-auto mb-5 block h-12 w-auto"
        />

        {/* Product name — 22px Aeonik 600. */}
        <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-charcoal">
          OJB Meeting Minutes
        </h1>

        {/* Subtitle — reframed for the signed-out state, same
            13px /65 type as sign-in. */}
        <p className="mb-5 text-[13px] text-charcoal/65">
          You&rsquo;re signed out of this session.
        </p>

        {/* Description panel — same cloud/50 panel as SignInPage so
            the post-signout context reads as a sibling, not a
            different surface. */}
        <div className="mb-6 rounded-2xl bg-cloud/50 px-[18px] py-3.5 text-left text-[13px] leading-[1.55] text-charcoal/80">
          Your Meeting Minutes session has ended. You&rsquo;re still signed in
          to Microsoft tenant-wide, so signing back in is one click - no
          password prompt needed.
        </div>

        {/* Sign back in button — identical chrome to SignInPage's
            Microsoft button (charcoal + cream + same hover lift)
            for visual continuity. Kept as a button (not an anchor)
            so the click is handled locally via clearJustSignedOut. */}
        <button
          type="button"
          onClick={clearJustSignedOut}
          className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-charcoal px-[18px] py-[13px] text-[14px] font-medium text-cream transition-all duration-200 hover:-translate-y-px hover:bg-ink hover:shadow-[0_8px_20px_rgba(43,42,47,0.30)]"
        >
          Sign back in
        </button>

        {/* Footer — parallel to the SignInPage restriction note, but
            with the context shifted to "come back anytime". */}
        <p className="mt-[18px] text-[11px] leading-[1.55] text-charcoal/55">
          Close this tab to finish, or sign back in to resume where you
          left off.
        </p>
      </div>

      {/* Locations line below the card — cream + 0.04em tracking +
          soft shadow so it reads cleanly on the photo. */}
      <p className="relative z-10 mt-8 text-center text-[11px] tracking-[0.04em] text-cream drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]">
        OJB Landscape Architecture · Boston · Dallas · Denver · Houston · Philadelphia · San Diego
      </p>
    </div>
  );
}
